/**
 * 純粋関数（DOM非依存）。net_precision に応じた日付表示・宿泊日程計算・ステータス整形。
 * app.ts と node のユニットテスト(scripts/test-format.mjs)の両方から使う。
 */

export type Tone = 'ok' | 'warn' | 'danger' | 'muted';

export interface PrecisionInfo {
  /** 表示バッジ文言 */
  label: string;
  /** 宿・便を実際に押さえてよい確度か（日付が確定しているか） */
  bookable: boolean;
  tone: Tone;
  /** 区分 */
  tier: 'time' | 'day' | 'week' | 'month' | 'quarter' | 'year' | 'tbd';
}

/** net_precision.abbrev → 予約妥当性の判定。宿を押さえるべきかの核。 */
export function precisionInfo(abbrev: string): PrecisionInfo {
  const a = (abbrev || '').toUpperCase();
  switch (a) {
    case 'SEC':
    case 'MIN':
      return { label: '日時確定', bookable: true, tone: 'ok', tier: 'time' };
    case 'HR':
      return { label: '時刻ほぼ確定', bookable: true, tone: 'ok', tier: 'time' };
    case 'DAY':
      return { label: '日付確定・時刻未定', bookable: true, tone: 'ok', tier: 'day' };
    case 'WK':
    case 'WEEK':
      return { label: '1週間以内・日未定', bookable: false, tone: 'warn', tier: 'week' };
    case 'M':
    case 'MO':
    case 'MONTH':
      return { label: '今月内・日未定', bookable: false, tone: 'warn', tier: 'month' };
    case 'Q1':
    case 'Q2':
    case 'Q3':
    case 'Q4':
      return { label: `第${a[1]}四半期のみ`, bookable: false, tone: 'warn', tier: 'quarter' };
    case 'Y':
    case 'YEAR':
      return { label: '年内・時期未定', bookable: false, tone: 'danger', tier: 'year' };
    default:
      return { label: '日程未定', bookable: false, tone: 'muted', tier: 'tbd' };
  }
}

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

export interface Ymd {
  y: number;
  m: number;
  d: number;
}

/** ISO(UTC)文字列 → JSTカレンダー日付(y/m/d) */
export function jstDate(iso: string): Ymd | null {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const s = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(t));
  const [y, m, d] = s.split('-').map(Number);
  return { y: y!, m: m!, d: d! };
}

/** ISO(UTC) → JSTの "HH:mm" */
function jstTime(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Tokyo',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

function weekdayOf({ y, m, d }: Ymd): string {
  return WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]!;
}

export function addDays({ y, m, d }: Ymd, delta: number): Ymd {
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

/** yyyy-mm-dd（楽天/ディープリンク用） */
export function isoDate({ y, m, d }: Ymd): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** "8/7(木)" 形式 */
export function shortJp(ymd: Ymd): string {
  return `${ymd.m}/${ymd.d}(${weekdayOf(ymd)})`;
}

export interface LaunchDateDisplay {
  /** 主表示 例 "2026年8月7日(木)" / "2026年 第1四半期" */
  main: string;
  /** 補助 例 "04:30 JST" / "日にち未定" */
  sub: string;
  precision: PrecisionInfo;
}

/** net(UTC ISO) と precision から、精度に応じた日付表示を作る。 */
export function formatLaunchDate(netIso: string, abbrev: string): LaunchDateDisplay {
  const p = precisionInfo(abbrev);
  const ymd = jstDate(netIso);
  if (!ymd) return { main: '日程未定', sub: '', precision: p };
  const dateStr = `${ymd.y}年${ymd.m}月${ymd.d}日(${weekdayOf(ymd)})`;

  switch (p.tier) {
    case 'time':
      // 分/秒/時 精度：日付＋時刻（JST）
      return { main: dateStr, sub: `${jstTime(netIso)} JST` + (abbrev.toUpperCase() === 'HR' ? '（時刻は目安）' : ''), precision: p };
    case 'day':
      return { main: dateStr, sub: '時刻未定', precision: p };
    case 'week':
      return { main: `${ymd.y}年${ymd.m}月ごろ`, sub: 'この頃（±数日・日付未定）', precision: p };
    case 'month':
      return { main: `${ymd.y}年${ymd.m}月`, sub: '日にち未定', precision: p };
    case 'quarter': {
      const q = abbrev.toUpperCase()[1] ?? '?';
      return { main: `${ymd.y}年 第${q}四半期`, sub: '時期未定', precision: p };
    }
    case 'year':
      return { main: `${ymd.y}年`, sub: '時期未定', precision: p };
    default:
      return { main: '日程未定', sub: '', precision: p };
  }
}

export interface StayPlan {
  checkIn: Ymd;
  checkOut: Ymd;
  checkInLabel: string;
  checkOutLabel: string;
  checkInIso: string;
  checkOutIso: string;
  nights: number;
  /** 打ち上げ日が未確定で日程が仮のとき true */
  tentative: boolean;
}

/**
 * 打ち上げ日を軸に 前泊pre日・後泊post日 の宿泊日程を計算。
 * checkIn = 打ち上げ日 - pre、checkOut = 打ち上げ日 + post。
 * precision が bookable でないときは tentative=true（UIで「仮」と明示する）。
 */
export function computeStay(netIso: string, abbrev: string, pre: number, post: number): StayPlan | null {
  const anchor = jstDate(netIso);
  if (!anchor) return null;
  const checkIn = addDays(anchor, -Math.max(0, pre));
  const checkOut = addDays(anchor, Math.max(0, post));
  return {
    checkIn,
    checkOut,
    checkInLabel: shortJp(checkIn),
    checkOutLabel: shortJp(checkOut),
    checkInIso: isoDate(checkIn),
    checkOutIso: isoDate(checkOut),
    nights: Math.max(1, pre + post),
    tentative: !precisionInfo(abbrev).bookable,
  };
}

export interface StatusInfo {
  label: string;
  tone: Tone;
}

/** LL2 status.abbrev/name → バッジ */
export function statusInfo(abbrev: string, name: string): StatusInfo {
  const a = (abbrev || '').toUpperCase();
  if (a === 'GO') return { label: 'Go（実施予定）', tone: 'ok' };
  if (a === 'TBC') return { label: 'TBC（暫定）', tone: 'warn' };
  if (a === 'TBD') return { label: 'TBD（日程調整中）', tone: 'muted' };
  if (a === 'SUCCESS') return { label: '成功', tone: 'ok' };
  if (a === 'FAILURE' || a === 'PARTIAL FAILURE') return { label: '失敗', tone: 'danger' };
  if (a === 'HOLD') return { label: 'ホールド', tone: 'warn' };
  if (a === 'IN FLIGHT') return { label: '飛行中', tone: 'ok' };
  return { label: name || abbrev || '—', tone: 'muted' };
}

/** テキストのHTMLエスケープ（LL2由来文字列をDOMに入れる前に必ず通す） */
export function escapeHtml(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
